import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  ClusterReport,
  ClusterTract,
  fetchPowerGeoJSON,
  fetchFloodZonesGeoJSON,
  fetchMineSubsidenceGeoJSON,
} from '../../lib/api';
import { REPORT } from '../print/PrintAtoms';
import { SANS, MONO, RULE, RULE_SOFT, Eyebrow } from './DossierSections';
import type { DossierPanel } from './keipDossier';

mapboxgl.accessToken = (import.meta.env.VITE_MAPBOX_TOKEN as string) || '';

type ToggleKey = 'transmission' | 'substations' | 'flood' | 'subsidence' | 'flat';

const TOGGLE_LABEL: Record<ToggleKey, string> = {
  transmission: 'Transmission',
  substations: 'Substations',
  flood: 'Flood zones',
  subsidence: 'Mine subsidence',
  flat: 'Flat ground',
};

// Flat-ground overlay (dossier-curated static asset): ground under 5% slope,
// with the portion on MOU tracts emphasized. Green family — distinct from the
// blue slope choropleth beneath it.
const FLAT_FILL = '#79A87E';
const FLAT_MOU_FILL = '#2F7D46';

// The tract extent is a tiny ~2 km box; the meaningful grid infrastructure (the
// 765 kV Baker–Broadford line at 2.2 mi, the 138 kV Cedar Creek substation at
// 3.8 mi) sits outside it. Pad the extent so the data fetch and the 'Grid'
// frame take in the interconnection picture.
const PAD_LON = 0.085;
const PAD_LAT = 0.065;
function padExtent([xmin, ymin, xmax, ymax]: [number, number, number, number]): [number, number, number, number] {
  return [xmin - PAD_LON, ymin - PAD_LAT, xmax + PAD_LON, ymax + PAD_LAT];
}

// Tracts render as neutral paper — the terrain story is told by the green
// flat-ground overlay, not a per-tract choropleth. Vacant = warm paper,
// occupied = gray (OCCUPIED_FILL below).
const VACANT_FILL = '#E9E5DD';

// Transmission colored/weighted by voltage: EHV (765 kV) reads as the headline,
// 138 kV as the interconnect tier, distribution (46 kV) as faint context.
const VNUM = ['to-number', ['get', 'voltage_kv'], 0];
const TX_COLOR: any = ['step', VNUM, '#B7BEC4', 138, '#5E93AE', 345, '#C98A3C', 765, '#E0A93B'];
const TX_WIDTH: any = ['step', VNUM, 1, 138, 2.3, 345, 3.4, 765, 5];
const SUB_RADIUS: any = ['step', VNUM, 3.5, 138, 6, 345, 8];
const SUB_COLOR: any = ['step', VNUM, '#8CA0AC', 138, '#3E6E86', 345, '#C4705A'];

// Pike County Regional Airport (Hatcher Field) — the park's private-jet / freight
// field, ~9 mi (about 20 min) north on a reclaimed ridge. A single curated
// landmark, so it renders always-on rather than as a toggle; the legend notes
// it sits 9 mi due north of the opening frame.
const AIRPORT_LNGLAT: [number, number] = [-82.566432, 37.561811];
const AIRPORT_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: AIRPORT_LNGLAT }, properties: { label: 'Pike County Rgnl · 9 mi' } }],
};

// A tract only reads as an opportunity if the county record has it vacant.
// 'occupied' / 'other' parcels are gray on the map and flagged in the list so
// the dossier never implies they're available.
const OCCUPIED_FILL = '#9AA7B0';
function isVacant(t: ClusterTract): boolean {
  return t.status === 'vacant';
}

function hardFlagCount(t: ClusterTract): number {
  return t.red_flags.filter((f) => f.severity === 'red').length;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);
}

function popupHtml(props: Record<string, any>): string {
  const tract = props.tract != null ? String(props.tract) : '–';
  const acreage = props.acreage != null ? Math.round(Number(props.acreage)).toLocaleString() : null;
  const status = props.status ? String(props.status) : null;
  const occupant = props.occupant ? String(props.occupant) : null;
  const availLine = status && status !== 'vacant'
    ? `<div style="margin:0 0 6px;color:#54636F;font-weight:600">${occupant ? 'Occupied · ' + escapeHtml(occupant) : status === 'occupied' ? 'Occupied — not available' : 'Not vacant'}</div>`
    : '';
  // Buildable = the tract's under-5%-slope share, shown only for available
  // tracts whose flat ground connects to the contiguous pad.
  const buildableLine = status === 'vacant' && !props.offPad && props.pct_u5 != null && props.acreage != null
    ? `<div style="color:#1F5A31;font-weight:600">${Math.round(Number(props.acreage) * Number(props.pct_u5) / 100)} ac flat (&lt;5% slope)</div>`
    : '';
  return `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;font-size:12px;line-height:1.6;color:#3E4E5C;min-width:170px">
      <div style="font-family:Georgia,'Times New Roman',serif;font-weight:400;font-size:16px;color:#2E3840;margin-bottom:6px">Tract ${escapeHtml(tract)}</div>
      ${availLine}
      ${acreage != null ? `<div>${acreage} ac</div>` : ''}
      ${buildableLine}
    </div>
  `;
}

function LegendRow({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 5 }}>
      <span style={{ width: 22, flexShrink: 0, display: 'inline-flex', justifyContent: 'center' }}>{swatch}</span>
      <span style={{ fontFamily: SANS, fontSize: 12.5, color: REPORT.body }}>{label}</span>
    </div>
  );
}

// Screening on the Blue Ridge blues — depth encodes severity, not hue. A clean
// tract reads as calm slate; flags deepen toward navy rather than turning red.
function ScreenBadge({ t }: { t: ClusterTract }) {
  const flags = t.red_flags;
  if (flags.length === 0) return <span style={{ fontFamily: SANS, fontSize: 10.5, fontWeight: 600, color: REPORT.highlight }}>Clean</span>;
  const hard = flags.find((f) => f.severity === 'red');
  const f = hard ?? flags[0];
  const tone = hard
    ? { bg: 'rgba(46,71,89,0.16)', fg: '#2E4759' }
    : { bg: 'rgba(80,98,114,0.12)', fg: '#46647B' };
  return (
    <span title={flags.map((x) => x.label).join(', ')} style={{ fontFamily: SANS, fontSize: 10, fontWeight: 600, color: tone.fg, background: tone.bg, border: `1px solid ${RULE}`, borderRadius: 999, padding: '1px 7px' }}>
      {f.label}
    </span>
  );
}

// Availability, for non-vacant tracts. Occupant name (when curated) makes the
// "not vacant" concrete — e.g. B1 · Wright Concrete.
function AvailabilityBadge({ t, occupant }: { t: ClusterTract; occupant?: string }) {
  const label = occupant
    ? `Occupied · ${occupant}`
    : t.status === 'occupied' ? 'Occupied' : 'Not vacant';
  return (
    <span title={label} style={{ fontFamily: SANS, fontSize: 10, fontWeight: 600, color: '#54636F', background: 'rgba(122,138,152,0.18)', border: `1px solid ${RULE}`, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// Park-wide infrastructure facts as collapsible cards for the map rail —
// collapsed shows a teaser, expanded shows every fact + the note. This is where
// the deeper utility narrative lives without crowding the page.
function SiteDataRail({ panels }: { panels: DossierPanel[] }) {
  // The first panel (the site's lead story — power, here) opens by default so
  // the rail reads at a glance instead of as a wall of closed drawers.
  const [open, setOpen] = useState<Record<string, boolean>>(
    () => (panels[0] ? { [panels[0].key]: true } : {})
  );
  return (
    <div style={{ border: `1px solid ${RULE}`, borderRadius: 6, overflow: 'hidden', maxHeight: 512, overflowY: 'auto' }}>
      {panels.map((p) => {
        const isOpen = !!open[p.key];
        const teaser = p.facts[0]?.value;
        return (
          <div key={p.key} style={{ borderBottom: `1px solid ${RULE_SOFT}` }}>
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [p.key]: !o[p.key] }))}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                padding: '10px 12px', background: isOpen ? REPORT.surface : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: REPORT.heading }}>{p.title}</div>
                {!isOpen && teaser && (
                  <div style={{ fontFamily: SANS, fontSize: 11.5, color: REPORT.secondary, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{teaser}</div>
                )}
              </div>
              <span style={{ flexShrink: 0, fontFamily: MONO, fontSize: 14, color: REPORT.tertiary, lineHeight: 1 }}>{isOpen ? '–' : '+'}</span>
            </button>
            {isOpen && (
              <div style={{ padding: '0 12px 12px' }}>
                {p.facts.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontFamily: SANS, fontSize: 12, padding: '3px 0', borderTop: i === 0 ? 'none' : `1px solid ${RULE_SOFT}` }}>
                    <span style={{ color: REPORT.secondary, flexShrink: 0 }}>{f.label}</span>
                    <span style={{ color: REPORT.heading, fontWeight: 600, textAlign: 'right' }}>{f.value}</span>
                  </div>
                ))}
                {p.note && <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 11, lineHeight: 1.45, color: REPORT.tertiary, fontStyle: 'italic' }}>{p.note}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ClusterMap({
  report, selectedId, onSelect, occupants, siteData, tractOverrides, flatOverlayUrl, mouTracts,
}: {
  report: ClusterReport;
  selectedId: string | null;
  onSelect: (id: string) => void;
  occupants?: Record<string, string>;
  siteData?: DossierPanel[];
  tractOverrides?: Record<string, { label?: string; status?: string; offPad?: boolean }>;
  flatOverlayUrl?: string;
  // Deal tracts (display labels, e.g. 'A-3') — tagged "MOU" in the tract list.
  mouTracts?: string[];
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const fetchedRef = useRef<Partial<Record<ToggleKey, boolean>>>({});
  const abortsRef = useRef<AbortController[]>([]);
  const hoverRef = useRef<(id: string | null) => void>(() => {});

  const [toggles, setToggles] = useState<Record<ToggleKey, boolean>>({
    transmission: true, substations: true, flood: false, subsidence: false, flat: true,
  });
  const [basemap, setBasemap] = useState<'map' | 'aerial'>('map');
  // Frame: 'site' opens tight on the tracts (the subject); 'grid' pulls back to
  // the padded extent so the 765 kV line and the 138 kV yard read in one frame.
  const [frame, setFrame] = useState<'site' | 'grid'>('site');
  const [railTab, setRailTab] = useState<'data' | 'tracts'>('data');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  hoverRef.current = setHoveredId;

  // Curated dossier overrides applied before anything renders from tract rows
  // (e.g. the state GIS codes the MOU's C-1 tract as '99' / status 'other').
  // Tract ids display in MOU / Exhibit A style (GIS 'A3' → 'A-3') so the map,
  // list, and deal copy speak one format; overrides/occupants key this label.
  const tracts = useMemo(() => report.tracts.map((t): ClusterTract & { offPad?: boolean } => {
    const label = t.tract.replace(/^([A-Z])(\d+)$/, '$1-$2');
    const o = tractOverrides?.[label];
    return o
      ? { ...t, tract: o.label ?? label, status: o.status ?? t.status, offPad: o.offPad }
      : { ...t, tract: label };
  }), [report.tracts, tractOverrides]);

  // Ranked: available (vacant) first, then hard-flagged (mine void / SFHA)
  // demoted, then by developable acres. Occupied/other tracts sink to the end.
  const rankedTracts = useMemo(() => {
    return [...tracts].sort((a, b) => {
      const av = isVacant(a) ? 0 : 1;
      const bv = isVacant(b) ? 0 : 1;
      if (av !== bv) return av - bv;
      const fa = hardFlagCount(a) > 0 ? 1 : 0;
      const fb = hardFlagCount(b) > 0 ? 1 : 0;
      if (fa !== fb) return fa - fb;
      return (b.developable_acres ?? -1) - (a.developable_acres ?? -1);
    });
  }, [tracts]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Defer creation one tick so React 18 StrictMode's mount → unmount → mount
    // settles before the map is built — creating on the first, immediately torn
    // down mount races Mapbox's async style loader and the survivor's `load`
    // never fires. Exactly one map is built, removed cleanly on real unmount.
    let mapInstance: mapboxgl.Map | null = null;
    const timer = setTimeout(() => {
      const tractFeatures: GeoJSON.Feature[] = tracts.map((t) => ({
        type: 'Feature',
        geometry: JSON.parse(t.geojson),
        properties: {
          parcel_id: t.parcel_id, tract: t.tract, acreage: t.acreage,
          avg_slope: t.avg_slope, pct_u5: t.pct_u5, offPad: t.offPad ?? false,
          status: t.status, occupant: occupants?.[t.tract] ?? null,
        },
      }));
      const tractsFc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: tractFeatures };

      const [xmin, ymin, xmax, ymax] = report.cluster.extent;

      const map = new mapboxgl.Map({
        container,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [(xmin + xmax) / 2, (ymin + ymax) / 2],
        zoom: 12,
        attributionControl: false,
        cooperativeGestures: true,
      });
      mapRef.current = map;
      mapInstance = map;

      // Guard against a double `load` fire on the same map instance (adding the
      // satellite raster source during load can re-enter): setup adds each
      // source/layer exactly once, so a second invocation is a no-op instead of
      // an "already exists" throw that aborts the rest of the wiring.
      let didSetup = false;
      const setup = () => {
        if (didSetup) return;
        didSetup = true;
        // Aerial: mapbox.satellite raster inserted below the first label layer so
        // road/place labels stay legible on top of the imagery. Toggled by the
        // Map/Aerial control — no setStyle, so the custom layers below survive.
        const firstSymbol = map.getStyle().layers?.find((l) => l.type === 'symbol')?.id;
        map.addSource('satellite', { type: 'raster', url: 'mapbox://mapbox.satellite', tileSize: 256 });
        map.addLayer({ id: 'satellite-raster', type: 'raster', source: 'satellite', layout: { visibility: 'none' }, paint: { 'raster-opacity': 1 } }, firstSymbol);

        // Transmission + substations — fetched into empty sources on first enable.
        map.addSource('transmission', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'transmission-line', type: 'line', source: 'transmission', layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': TX_COLOR, 'line-width': TX_WIDTH, 'line-opacity': 0.92 } });

        map.addSource('substations', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'substations-circle', type: 'circle', source: 'substations', layout: { visibility: 'none' }, paint: { 'circle-radius': SUB_RADIUS, 'circle-color': SUB_COLOR, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1.3, 'circle-opacity': 0.95 } });
        map.addLayer({
          id: 'substations-label', type: 'symbol', source: 'substations',
          filter: ['>=', VNUM, 138], layout: { visibility: 'none',
            'text-field': ['concat', ['get', 'name'], '  ', ['to-string', ['get', 'voltage_kv']], ' kV'],
            'text-size': 10, 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
            'text-offset': [0, 1.1], 'text-anchor': 'top', 'text-allow-overlap': false, 'text-optional': true,
          },
          paint: { 'text-color': REPORT.heading, 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 },
        });

        // Airport — a single curated landmark (Pike County Regional / Hatcher
        // Field), always on. Terracotta point + a distance label so the "how
        // close is the jet field" answer reads straight off the map.
        map.addSource('airport', { type: 'geojson', data: AIRPORT_FC });
        map.addLayer({ id: 'airport-marker', type: 'circle', source: 'airport', paint: { 'circle-radius': 6.5, 'circle-color': REPORT.accent, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1.8, 'circle-opacity': 0.96 } });
        map.addLayer({
          id: 'airport-label', type: 'symbol', source: 'airport',
          layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'], 'text-offset': [0, 1.15], 'text-anchor': 'top', 'text-allow-overlap': true },
          paint: { 'text-color': REPORT.heading, 'text-halo-color': '#ffffff', 'text-halo-width': 1.8 },
        });

        // Tracts — neutral paper so the flat-ground greens carry the terrain story.
        map.addSource('tracts', { type: 'geojson', data: tractsFc });
        map.addLayer({ id: 'tracts-fill', type: 'fill', source: 'tracts', paint: {
          'fill-color': ['case', ['==', ['get', 'status'], 'vacant'], VACANT_FILL, OCCUPIED_FILL],
          'fill-opacity': ['case', ['==', ['get', 'status'], 'vacant'], 0.35, 0.5],
        } });
        // Flat-ground overlay (static curated asset; only wired when the dossier
        // provides one): light wash = all <5% ground, dark green + solid edge =
        // the contiguous flat on MOU tracts. Sits below tract lines/labels.
        map.addSource('flatground', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'flat-fill', type: 'fill', source: 'flatground', filter: ['==', ['get', 'kind'], 'flat'], paint: { 'fill-color': FLAT_FILL, 'fill-opacity': 0.38 } });
        map.addLayer({ id: 'flat-line', type: 'line', source: 'flatground', filter: ['==', ['get', 'kind'], 'flat'], paint: { 'line-color': '#5E8A63', 'line-width': 1, 'line-opacity': 0.55, 'line-dasharray': [2, 2] } });
        map.addLayer({ id: 'flat-mou-fill', type: 'fill', source: 'flatground', filter: ['==', ['get', 'kind'], 'mou_flat'], paint: { 'fill-color': FLAT_MOU_FILL, 'fill-opacity': 0.5 } });
        map.addLayer({ id: 'flat-mou-line', type: 'line', source: 'flatground', filter: ['==', ['get', 'kind'], 'mou_flat'], paint: { 'line-color': '#1F5A31', 'line-width': 2.4, 'line-opacity': 0.95 } });
        map.addLayer({
          id: 'flat-label', type: 'symbol', source: 'flatground', filter: ['==', ['get', 'kind'], 'label'],
          layout: { 'text-field': ['get', 'text'], 'text-size': 11.5, 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'], 'text-allow-overlap': true },
          paint: { 'text-color': '#1F5A31', 'text-halo-color': 'rgba(255,255,255,0.92)', 'text-halo-width': 1.4 },
        });

        map.addLayer({ id: 'tracts-line', type: 'line', source: 'tracts', paint: { 'line-color': '#2E3840', 'line-width': 1, 'line-opacity': 0.6 } });
        // Tract ids on the map itself — the deal copy names A-3/A-6 etc., so a
        // reader can locate them without hovering.
        map.addLayer({
          id: 'tracts-label', type: 'symbol', source: 'tracts',
          layout: { 'text-field': ['get', 'tract'], 'text-size': 10.5, 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'] },
          paint: { 'text-color': '#2E3840', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.3 },
        });
        map.addLayer({ id: 'tracts-hover-line', type: 'line', source: 'tracts', filter: ['==', ['get', 'parcel_id'], '__none__'], paint: { 'line-color': REPORT.heading, 'line-width': 2, 'line-opacity': 0.9 } });
        map.addLayer({ id: 'tracts-selected-line', type: 'line', source: 'tracts', filter: ['==', ['get', 'parcel_id'], '__none__'], paint: { 'line-color': REPORT.accent, 'line-width': 3.5, 'line-opacity': 1 } });

        map.addSource('flood', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'flood-fill', type: 'fill', source: 'flood', layout: { visibility: 'none' }, paint: { 'fill-color': '#4f6d86', 'fill-opacity': 0.3 } });
        map.addSource('subsidence', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'subsidence-fill', type: 'fill', source: 'subsidence', layout: { visibility: 'none' }, paint: { 'fill-color': '#7c4a2d', 'fill-opacity': 0.38 } });

        map.on('click', 'tracts-fill', (ev) => {
          const id = ev.features?.[0]?.properties?.parcel_id as string | undefined;
          if (!id) return;
          new mapboxgl.Popup({ offset: 8, maxWidth: '240px' }).setLngLat(ev.lngLat).setHTML(popupHtml(ev.features![0].properties || {})).addTo(map);
          onSelectRef.current(id);
        });
        map.on('mousemove', 'tracts-fill', (ev) => {
          const id = ev.features?.[0]?.properties?.parcel_id as string | undefined;
          map.getCanvas().style.cursor = 'pointer';
          hoverRef.current(id ?? null);
        });
        map.on('mouseleave', 'tracts-fill', () => { map.getCanvas().style.cursor = ''; hoverRef.current(null); });

        // Open tight on the parcels — they're the subject. Context layers
        // (power, the airport ~9 mi N) sit outside this frame and read on
        // zoom-out / toggle; the data-fetch bbox stays wide regardless.
        const mLon = (xmax - xmin) * 0.3, mLat = (ymax - ymin) * 0.3;
        map.fitBounds([[xmin - mLon, ymin - mLat], [xmax + mLon, ymax + mLat]], { padding: 26, maxZoom: 15, duration: 0 });
        setMapReady(true);
      };
      map.on('load', setup);
    }, 0);

    return () => {
      clearTimeout(timer);
      abortsRef.current.forEach((c) => c.abort());
      if (mapInstance) { mapInstance.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.cluster.slug]);

  // Selected + hovered highlights (map click / sidebar hover both feed these).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer('tracts-selected-line')) return;
    map.setFilter('tracts-selected-line', ['==', ['get', 'parcel_id'], selectedId ?? '__none__']);
  }, [selectedId, mapReady]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer('tracts-hover-line')) return;
    map.setFilter('tracts-hover-line', ['==', ['get', 'parcel_id'], hoveredId ?? '__none__']);
  }, [hoveredId, mapReady]);

  // Aerial toggle — flip the satellite raster's visibility (no setStyle).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer('satellite-raster')) return;
    map.setLayoutProperty('satellite-raster', 'visibility', basemap === 'aerial' ? 'visible' : 'none');
  }, [basemap, mapReady]);

  // Frame toggle — refit between the tight site frame and the grid-context frame.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const [xmin, ymin, xmax, ymax] = report.cluster.extent;
    if (frame === 'site') {
      const mLon = (xmax - xmin) * 0.3, mLat = (ymax - ymin) * 0.3;
      map.fitBounds([[xmin - mLon, ymin - mLat], [xmax + mLon, ymax + mLat]], { padding: 26, maxZoom: 15 });
    } else {
      const [pxmin, pymin, pxmax, pymax] = padExtent(report.cluster.extent);
      map.fitBounds([[pxmin, pymin], [pxmax, pymax]], { padding: 18 });
    }
  }, [frame, mapReady, report.cluster.extent]);

  // Layer toggles: visibility + lazy-fetch on first enable (power layers default ON).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const setVisible = (layerId: string, visible: boolean) => { if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none'); };
    setVisible('transmission-line', toggles.transmission);
    setVisible('substations-circle', toggles.substations);
    setVisible('substations-label', toggles.substations);
    setVisible('flood-fill', toggles.flood);
    setVisible('subsidence-fill', toggles.subsidence);
    setVisible('flat-fill', toggles.flat && !!flatOverlayUrl);
    setVisible('flat-line', toggles.flat && !!flatOverlayUrl);
    setVisible('flat-mou-fill', toggles.flat && !!flatOverlayUrl);
    setVisible('flat-mou-line', toggles.flat && !!flatOverlayUrl);
    setVisible('flat-label', toggles.flat && !!flatOverlayUrl);

    const bbox = padExtent(report.cluster.extent).join(',');
    const setData = (sourceId: string, fc: GeoJSON.FeatureCollection) => { (map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined)?.setData(fc); };
    const lazy = (key: ToggleKey, run: (signal: AbortSignal) => Promise<void>) => {
      if (!toggles[key] || fetchedRef.current[key]) return;
      fetchedRef.current[key] = true;
      const controller = new AbortController();
      abortsRef.current.push(controller);
      run(controller.signal).catch(() => {});
    };
    lazy('transmission', (s) => fetchPowerGeoJSON('transmission_line', bbox, s).then((fc) => setData('transmission', fc)));
    lazy('substations', (s) => fetchPowerGeoJSON('substation', bbox, s, 100).then((fc) => setData('substations', fc)));
    lazy('flood', (s) => fetchFloodZonesGeoJSON(bbox, undefined, s).then((fc) => setData('flood', fc)));
    lazy('subsidence', (s) => fetchMineSubsidenceGeoJSON(bbox, s).then((fc) => setData('subsidence', fc)));
    if (flatOverlayUrl) {
      lazy('flat', (s) => fetch(flatOverlayUrl, { signal: s }).then((r) => r.json()).then((fc) => setData('flatground', fc)));
    }
  }, [toggles, mapReady, report.cluster.extent, flatOverlayUrl]);

  // Selecting a tract (map click or list click) pulls the rail to the Tracts
  // tab and scrolls that row into view — one tick later so the row has mounted.
  useEffect(() => {
    if (!selectedId) return;
    setRailTab('tracts');
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-tract-row="${selectedId}"]`);
      if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
    return () => clearTimeout(id);
  }, [selectedId]);

  const segBtn = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '4px 0', fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: active ? REPORT.white : REPORT.secondary, background: active ? REPORT.heading : 'transparent',
    border: 'none', cursor: 'pointer',
  });

  const hasSiteData = !!siteData?.length;
  const railTabEff: 'data' | 'tracts' = hasSiteData ? railTab : 'tracts';

  return (
    <section style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <Eyebrow color={REPORT.heading}>Interconnection map · {report.tracts.length} tracts</Eyebrow>
        <div style={{ fontFamily: SANS, fontSize: 11.5, color: REPORT.tertiary }}>Click a tract for acreage & screening</div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
        {/* Map with an on-map legend overlay */}
        <div style={{ flex: '1 1 560px', minWidth: 0, position: 'relative' }}>
          <div ref={containerRef} style={{ height: 580, width: '100%', borderRadius: 6, overflow: 'hidden', border: `1px solid ${RULE}` }} />
          <div style={{ position: 'absolute', left: 12, top: 12, background: 'rgba(228,230,234,0.94)', border: `1px solid ${RULE}`, borderRadius: 6, padding: '11px 14px', backdropFilter: 'blur(2px)' }}>
            <div style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: REPORT.tertiary, marginBottom: 6 }}>Legend</div>
            <LegendRow swatch={<span style={{ width: 20, height: 5, background: '#E0A93B', borderRadius: 2 }} />} label="765 kV EHV" />
            <LegendRow swatch={<span style={{ width: 20, height: 4, background: '#5E93AE', borderRadius: 2 }} />} label="138 kV" />
            <LegendRow swatch={<span style={{ width: 20, height: 2, background: '#B7BEC4', borderRadius: 2 }} />} label="46 kV local" />
            <LegendRow swatch={<span style={{ width: 12, height: 12, borderRadius: 12, background: '#3E6E86', border: '1.5px solid #fff' }} />} label="Substation" />
            <LegendRow swatch={<span style={{ width: 12, height: 12, borderRadius: 12, background: REPORT.accent, border: '1.5px solid #fff' }} />} label="Private-jet airport (9 mi due north of site)" />
            {flatOverlayUrl && <LegendRow swatch={<span style={{ width: 15, height: 10, background: FLAT_FILL, borderRadius: 2, opacity: 0.55 }} />} label="Flat ground (<5%)" />}
            {flatOverlayUrl && <LegendRow swatch={<span style={{ width: 15, height: 10, background: FLAT_MOU_FILL, borderRadius: 2, opacity: 0.8, outline: '1.5px solid #1F5A31' }} />} label="Contiguous flat · MOU tracts" />}
            <LegendRow swatch={<span style={{ width: 15, height: 10, background: VACANT_FILL, borderRadius: 2, border: '1px solid rgba(46,56,64,0.35)' }} />} label="Vacant tract" />
            <LegendRow swatch={<span style={{ width: 15, height: 10, background: OCCUPIED_FILL, borderRadius: 2 }} />} label="Occupied / not available" />
            {toggles.flood && <LegendRow swatch={<span style={{ width: 15, height: 10, background: '#4f6d86', opacity: 0.5, borderRadius: 2 }} />} label="100-yr floodplain (FEMA)" />}
            {toggles.subsidence && <LegendRow swatch={<span style={{ width: 15, height: 10, background: '#7c4a2d', opacity: 0.55, borderRadius: 2 }} />} label="Underground mine voids" />}
          </div>
        </div>

        {/* Sidebar: basemap toggle, layers, per-tract list */}
        <div style={{ width: 306, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: REPORT.tertiary, marginBottom: 5 }}>Base map</div>
              <div style={{ display: 'flex', border: `1px solid ${RULE}`, borderRadius: 4, overflow: 'hidden' }}>
                <button type="button" style={segBtn(basemap === 'map')} onClick={() => setBasemap('map')}>Map</button>
                <button type="button" style={segBtn(basemap === 'aerial')} onClick={() => setBasemap('aerial')}>Aerial</button>
              </div>
            </div>
            <div>
              <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: REPORT.tertiary, marginBottom: 5 }}>Frame</div>
              <div style={{ display: 'flex', border: `1px solid ${RULE}`, borderRadius: 4, overflow: 'hidden' }}>
                <button type="button" style={segBtn(frame === 'site')} onClick={() => setFrame('site')}>Site</button>
                <button type="button" style={segBtn(frame === 'grid')} onClick={() => setFrame('grid')}>Grid</button>
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: REPORT.tertiary, marginBottom: 5 }}>Layers</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px' }}>
              {(Object.keys(TOGGLE_LABEL) as ToggleKey[]).filter((key) => key !== 'flat' || !!flatOverlayUrl).map((key) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: SANS, fontSize: 11.5, color: REPORT.body, cursor: 'pointer' }}>
                  <input type="checkbox" checked={toggles[key]} onChange={() => setToggles((t) => ({ ...t, [key]: !t[key] }))} style={{ accentColor: REPORT.accent }} />
                  {TOGGLE_LABEL[key]}
                </label>
              ))}
            </div>
          </div>

          {hasSiteData && (
            <div style={{ display: 'flex', border: `1px solid ${RULE}`, borderRadius: 4, overflow: 'hidden' }}>
              <button type="button" style={segBtn(railTabEff === 'data')} onClick={() => setRailTab('data')}>Site data</button>
              <button type="button" style={segBtn(railTabEff === 'tracts')} onClick={() => setRailTab('tracts')}>Tracts</button>
            </div>
          )}

          {railTabEff === 'data' && siteData ? (
            <SiteDataRail panels={siteData} />
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, border: `1px solid ${RULE}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: REPORT.tertiary, padding: '9px 12px', borderBottom: `1px solid ${RULE}`, background: REPORT.surface }}>
              Tracts · by developable acres (under 10% slope)
            </div>
            <div style={{ overflowY: 'auto', maxHeight: 470 }}>
              {rankedTracts.map((t) => {
                const isSel = selectedId === t.parcel_id;
                const isHov = hoveredId === t.parcel_id;
                return (
                  <div
                    key={t.parcel_id}
                    data-tract-row={t.parcel_id}
                    onMouseEnter={() => setHoveredId(t.parcel_id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => onSelect(t.parcel_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer',
                      borderBottom: `1px solid ${RULE_SOFT}`,
                      background: isSel ? REPORT.surface : isHov ? RULE_SOFT : 'transparent',
                    }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: isVacant(t) ? FLAT_FILL : OCCUPIED_FILL, border: '1px solid rgba(0,0,0,0.12)' }} />
                    <div style={{ minWidth: 0, flex: 1, opacity: isVacant(t) ? 1 : 0.72 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: REPORT.heading, display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                          {t.tract}
                          {mouTracts?.includes(t.tract) && (
                            <span title="Under MOU to MD Squared Power" style={{
                              fontFamily: MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.08em',
                              color: '#1F5A31', background: 'rgba(47,125,70,0.12)', border: '1px solid rgba(31,90,49,0.3)',
                              borderRadius: 3, padding: '1px 5px',
                            }}>MOU</span>
                          )}
                        </span>
                        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 700, color: REPORT.heading, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {t.developable_acres != null ? t.developable_acres.toFixed(1) : '–'} <span style={{ fontSize: 10, fontWeight: 400, color: REPORT.tertiary }}>dev ac</span>
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 3 }}>
                        <span style={{ fontFamily: SANS, fontSize: 11, color: REPORT.secondary }}>
                          {Math.round(t.acreage)} ac · {t.avg_slope != null ? `${t.avg_slope.toFixed(1)}%` : '–'} slope
                        </span>
                        {isVacant(t) ? <ScreenBadge t={t} /> : <AvailabilityBadge t={t} occupant={occupants?.[t.tract]} />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>
      </div>
    </section>
  );
}
